import * as THREE from "three";
import { getItemPixelGrid } from "../ui/icons";

/**
 * Builds an authentic Minecraft 3D extruded voxel BufferGeometry for any item id.
 * Uses exact 16x16 pixel grids with directional shading baked into vertex colors.
 */
export function createItemBufferGeometry(id: number): THREE.BufferGeometry {
  const grid = getItemPixelGrid(id);
  if (!grid) {
    return new THREE.BoxGeometry(0.2, 0.2, 0.2);
  }
  return createItemBufferGeometryFromGrid(grid);
}

/**
 * Builds an authentic Minecraft 3D extruded voxel BufferGeometry from a 16x16 pixel grid.
 */
export function createItemBufferGeometryFromGrid(
  grid: (string | null)[][],
): THREE.BufferGeometry {

  // Exact Minecraft item model specifications:
  // Standard 16x16 model space where 16 pixels = 1.0 block unit (1 pixel = 1/16 = 0.0625).
  // Model is extruded by 1 pixel thickness (0.0625) centered on Z = 0.5 (Z in [7.5/16, 8.5/16]).
  // In Minecraft Java Edition (ItemModelGenerator.java & FaceBakery.java),
  // normalized coordinates span [0.0, 1.0] with origin (0, 0) at the bottom-left handle base.
  const pixelSize = 0.0625;
  const thickness = 0.0625;
  const hz = thickness / 2;
  const z0 = 0.5 - hz; // 0.46875 (7.5 / 16)
  const z1 = 0.5 + hz; // 0.53125 (8.5 / 16)

  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];

  function addQuad(
    p0: [number, number, number],
    p1: [number, number, number],
    p2: [number, number, number],
    p3: [number, number, number],
    norm: [number, number, number],
    r: number,
    g: number,
    b: number,
    shade: number,
  ) {
    const base = positions.length / 3;
    positions.push(...p0, ...p1, ...p2, ...p3);
    for (let i = 0; i < 4; i++) {
      normals.push(...norm);
      colors.push(r * shade, g * shade, b * shade);
    }
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const colorHex = grid[y][x];
      if (!colorHex) continue;

      const hex = parseInt(colorHex.slice(1), 16);
      const r = ((hex >> 16) & 0xff) / 255;
      const g = ((hex >> 8) & 0xff) / 255;
      const b = (hex & 0xff) / 255;

      const topTrans = y === 0 || grid[y - 1][x] === null;
      const bottomTrans = y === 15 || grid[y + 1][x] === null;
      const leftTrans = x === 0 || grid[y][x - 1] === null;
      const rightTrans = x === 15 || grid[y][x + 1] === null;

      const x0 = x * pixelSize;
      const x1 = x0 + pixelSize;
      const y0 = (15 - y) * pixelSize;
      const y1 = y0 + pixelSize;

      // Front (+Z)
      addQuad(
        [x0, y0, z1],
        [x1, y0, z1],
        [x1, y1, z1],
        [x0, y1, z1],
        [0, 0, 1],
        r, g, b, 1.0,
      );

      // Back (-Z)
      addQuad(
        [x1, y0, z0],
        [x0, y0, z0],
        [x0, y1, z0],
        [x1, y1, z0],
        [0, 0, -1],
        r, g, b, 0.95,
      );

      // Top (+Y)
      if (topTrans) {
        addQuad(
          [x0, y1, z1],
          [x1, y1, z1],
          [x1, y1, z0],
          [x0, y1, z0],
          [0, 1, 0],
          r, g, b, 1.0,
        );
      }

      // Bottom (-Y)
      if (bottomTrans) {
        addQuad(
          [x0, y0, z0],
          [x1, y0, z0],
          [x1, y0, z1],
          [x0, y0, z1],
          [0, -1, 0],
          r, g, b, 0.60,
        );
      }

      // Right (+X)
      if (rightTrans) {
        addQuad(
          [x1, y0, z1],
          [x1, y0, z0],
          [x1, y1, z0],
          [x1, y1, z1],
          [1, 0, 0],
          r, g, b, 0.80,
        );
      }

      // Left (-X)
      if (leftTrans) {
        addQuad(
          [x0, y0, z0],
          [x0, y0, z1],
          [x0, y1, z1],
          [x0, y1, z0],
          [-1, 0, 0],
          r, g, b, 0.80,
        );
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new THREE.Float32BufferAttribute(normals, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}
